<template>
  <div class="wrong-word-detail">
    <div class="page-title">一般错别字发文详情</div>
    <div v-if="publishTime" class="time-range">
      统计日期：{{ displayDate }}
      <span v-if="ready && total" class="summary">共 {{ total }} 篇发文</span>
    </div>

    <van-list
      v-if="ready"
      v-model="loading"
      :finished="finished"
      :finished-text="finishedText"
      @load="onLoad"
    >
      <div class="article-list">
        <article v-for="item in articles" :key="item.id" class="article-card">
          <h2 class="article-title">{{ item.title || "无标题文章" }}</h2>
          <div class="article-meta">
            <span>{{ formatTime(item.publishTime) }}</span>
            <span>{{ item.siteName || "未知信源" }}</span>
            <span>{{ mediaName(item) }}</span>
          </div>

          <div class="word-list">
            <div
              v-for="(word, index) in visibleWords(item)"
              :key="item.id + '-' + index"
              class="word-item"
            >
              <div v-if="sourceText(word)" class="word-field">
                <span class="field-label">来源：</span>
                <span class="field-value">{{ sourceText(word) }}</span>
              </div>
              <div class="word-field">
                <span class="field-label">错敏分类：</span>
                <span class="field-value">{{ classifyName(word) }}</span>
              </div>
              <div class="word-field">
                <span class="field-label">错敏词：</span>
                <span class="field-value problem-word">{{ word.problemWord || "-" }}</span>
              </div>
              <div v-if="word.recommendWord" class="word-field">
                <span class="field-label">推荐修改词：</span>
                <span class="field-value recommend-word">{{ word.recommendWord }}</span>
              </div>
              <div v-if="word.reason" class="word-field">
                <span class="field-label">修改原因：</span>
                <span class="field-value">{{ word.reason }}</span>
              </div>
            </div>
          </div>

          <div
            v-if="wordCount(item) > collapseSize"
            class="word-toggle"
            @click="toggleExpand(item)"
          >
            {{ isExpanded(item) ? "收起" : "展开全部（共 " + wordCount(item) + " 处）" }}
          </div>

          <div v-if="!wordCount(item)" class="no-word">暂无可展示的错词详情</div>
          <a v-if="item.url" class="origin-link" :href="item.url" target="_blank">查看原文</a>
        </article>
      </div>
    </van-list>

    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    <div v-if="finished && !articles.length && !errorMessage" class="nodata-box">
      <p>暂无数据</p>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { List, Toast } from "vant";
import {
  getWechatCode,
  getWrongWordStatisticsDetail,
} from "@/api/wrongWordStatisticsDetail";

export default {
  name: "wrongWordStatisticsDetail",
  components: {
    "van-list": List,
  },
  data() {
    return {
      ready: false,
      loading: false,
      finished: false,
      finishedText: "",
      errorMessage: "",
      articles: [],
      total: 0,
      openId: "",
      publishTime: "",
      pageNo: 0,
      pageSize: 10,
      // 单篇错词默认展示条数，超出则折叠，点“展开全部”查看
      collapseSize: 3,
      // 记录每篇文章的展开状态，key 为文章 id
      expandedMap: {},
      siteTypeNames: {
        "01": "网站",
        "04": "微博",
        "06": "微信",
        "11": "短视频",
        "12": "头条号",
        "13": "贴吧",
        "14": "百家号",
        "15": "搜狐号",
        "16": "网易号",
        "17": "企鹅号",
        "18": "大风号",
        "19": "东方号",
      },
    };
  },
  computed: {
    displayDate() {
      return moment(this.publishTime, "YYYYMMDD").format("YYYY-MM-DD");
    },
  },
  created() {
    const icon = document.querySelector("link.linkico");
    if (icon) icon.href = "./wmico.ico";
  },
  mounted() {
    this.initPage();
  },
  methods: {
    initPage() {
      const query = this.$route.query || {};
      const stateDate = String(query.state || "").match(/20\d{6}/);
      this.publishTime = query.publishTime || (stateDate ? stateDate[0] : "");
      this.openId =
        query.openId ||
        query.openid ||
        localStorage.getItem("wlgl-openId") ||
        "";

      if (!/^20\d{6}$/.test(this.publishTime)) {
        this.errorMessage = "缺少正确的统计日期";
        return;
      }
      // 微信回调中的 code/state 优先于本地 Token，避免复用 Redis 中已过期的隔夜 Token。
      if (query.code && query.state) {
        this.queryCode(query.code, query.state);
        return;
      }
      if (localStorage.getItem("wlgl-token") && this.openId) {
        localStorage.setItem("wlgl-openId", this.openId);
        this.ready = true;
        return;
      }
      this.errorMessage = "登录信息已失效，请从微信预警消息重新进入";
    },
    queryCode(code, state) {
      Toast.loading({ message: "加载中...", forbidClick: true });
      getWechatCode({ code, state })
        .then((res) => {
          Toast.clear();
          const body = res.data || {};
          if (body.code !== 200 || !body.data) {
            this.errorMessage = body.message || "登录失败";
            return;
          }
          localStorage.setItem("wlgl-token", body.data);
          this.openId = body.openId || this.openId;
          localStorage.setItem("wlgl-openId", this.openId);

          // OAuth code 只能短时使用；兑换成功后从地址栏移除，避免刷新时重复兑换。
          const nextQuery = Object.assign({}, this.$route.query, {
            publishTime: this.publishTime,
            openId: this.openId,
          });
          delete nextQuery.code;
          delete nextQuery.state;
          delete nextQuery.openid;
          this.$router.replace(
            { path: this.$route.path, query: nextQuery },
            () => {},
            () => {}
          );
          this.ready = true;
        })
        .catch(() => {
          Toast.clear();
          localStorage.removeItem("wlgl-token");
          this.errorMessage = "登录失败，请稍后重试";
        });
    },
    onLoad() {
      if (this.finished || !this.openId || !this.publishTime) {
        this.loading = false;
        return;
      }
      const nextPage = this.pageNo + 1;
      getWrongWordStatisticsDetail({
        openId: this.openId,
        publishTime: this.publishTime,
        pageNo: nextPage,
        pageSize: this.pageSize,
      })
        .then((res) => {
          const body = res.data || {};
          if (body.code === 20009) {
            localStorage.removeItem("wlgl-token");
            localStorage.removeItem("wlgl-openId");
            this.handleLoadError("登录信息已失效，请从微信预警消息重新进入");
            return;
          }
          if (body.code !== 200) {
            this.handleLoadError(body.message || "获取详情失败");
            return;
          }
          const page = body.data || {};
          const records = Array.isArray(page.records) ? page.records : [];
          // 预先收集每篇文章的错词，避免模板多次重复计算
          records.forEach((record) => {
            record._words = this.collectWords(record);
          });
          this.pageNo = nextPage;
          this.total = Number(page.total) || 0;
          this.articles = this.articles.concat(records);
          this.finished =
            records.length < this.pageSize ||
            (this.total > 0 && this.articles.length >= this.total);
          this.finishedText = this.articles.length && this.finished ? "没有更多了" : "";
          this.loading = false;
        })
        .catch((error) => {
          const message =
            error && error.response && error.response.data
              ? error.response.data.message
              : "网络异常，请稍后重试";
          this.handleLoadError(message);
        });
    },
    handleLoadError(message) {
      this.loading = false;
      this.finished = true;
      this.finishedText = "";
      this.errorMessage = message;
      Toast.fail(message);
    },
    collectWords(article) {
      const words = Array.isArray(article.wordList) ? article.wordList.slice() : [];
      const appendGroupWords = (groups, sourceType) => {
        (Array.isArray(groups) ? groups : []).forEach((group) => {
          (Array.isArray(group.words) ? group.words : []).forEach((word) => {
            words.push(Object.assign({ sourceType }, word));
          });
        });
      };
      appendGroupWords(article.ocrMessages, "OCR");
      appendGroupWords(article.attMessages, "附件");
      return words;
    },
    articleWords(article) {
      return Array.isArray(article._words) ? article._words : [];
    },
    wordCount(article) {
      return this.articleWords(article).length;
    },
    visibleWords(article) {
      const words = this.articleWords(article);
      return this.isExpanded(article) ? words : words.slice(0, this.collapseSize);
    },
    isExpanded(article) {
      return !!this.expandedMap[article.id];
    },
    toggleExpand(article) {
      this.$set(this.expandedMap, article.id, !this.expandedMap[article.id]);
    },
    sourceText(word) {
      const type = word.errorSourceType == null ? "" : String(word.errorSourceType);
      if (type === "1") return "系统判断错误词";
      if (type === "2") return "自定义错敏词库";
      return "";
    },
    classifyName(word) {
      return word.secondLevelName || word.firstLevelName || "";
    },
    mediaName(article) {
      return (
        article.videoTypeName ||
        article.siteTypeName ||
        this.siteTypeNames[article.siteType] ||
        "其他"
      );
    },
    formatTime(value) {
      return value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "";
    },
  },
};
</script>

<style scoped>
@import url("./wrongWordStatisticsDetail.css");
</style>
